import { useIntl } from 'react-intl'
import DataTable, {
	TableBody,
	TableColumn,
	TableHeader,
	TableRow,
} from 'react-md/lib/DataTables'
import OverflowGradient from 'ui/components/OverflowGradient'
import { TableContainer } from 'ui/StyledComponents/Tables'
import type { LoanEventSorting } from '../types'
import { Fragment } from 'react'
import { loanEventTypeMessage } from '../messages'
import styled from 'styled-components'
import { formatDate } from 'util/formatStrings'
import FontIcon from 'react-md/lib/FontIcons'
import { loanEventTableHeaders } from './constants'
import naturalSort from 'javascript-natural-sort'
import type { ApartmentSelectOptions } from '@viilu-web/shared/types/apartment'
import type { LoanEvent } from '@viilu-web/shared/types/loans'
import { LoanEventType } from '@viilu-web/shared'

const LoanEventRow = styled(TableRow)<{ $filledBackground: boolean }>`
	background-color: ${(props) => (props.$filledBackground ? '#FAFAFA' : '')};
`

const LoanEventsNotFoundContainer = styled.div`
	display: flex;
	justify-content: center;
	align-items: center;
	height: 200px;
`

type LoanEventsTableProps = {
	events: LoanEvent[]
	apartmentOptions: ApartmentSelectOptions[]
	sort: LoanEventSorting
	onEventClick: (event: LoanEvent) => void
	changeSort: (column: keyof LoanEvent, type?: string) => void
}

function LoanEventsTable(props: LoanEventsTableProps) {
	const { events, apartmentOptions, sort, onEventClick, changeSort } = props
	const intl = useIntl()

	const renderTableHeaders = () => {
		return (
			<TableRow>
				{loanEventTableHeaders.map((header) => (
					<TableColumn
						key={header.key}
						onClick={() => changeSort(header.key as keyof LoanEvent)}
					>
						<div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
							<p className="text-strong text-subtle">{header.title(intl)}</p>
							{sort.column === header.key && (
								<FontIcon style={{ fontSize: 12 }}>
									{sort.order === 'asc' ? 'arrow_upward' : 'arrow_downward'}
								</FontIcon>
							)}
						</div>
					</TableColumn>
				))}
			</TableRow>
		)
	}

	return (
		<>
			{events?.length > 0 ? (
				<TableContainer>
					<DataTable
						baseId="loan-events-table"
						plain
						className="md-paper--1"
						style={{
							background: 'white',
						}}
					>
						<TableHeader
							style={{
								position: 'sticky',
								top: 0,
								zIndex: 1,
								background: 'white',
								borderBottom: 'rgba(0, 0, 0, 0.12) solid 2px',
							}}
						>
							{renderTableHeaders()}
						</TableHeader>
						<TableBody>
							{events
								.sort((a, b) => {
									const aValue = a[sort.column]
									const bValue = b[sort.column]
									return sort.order === 'asc'
										? naturalSort(aValue, bValue)
										: naturalSort(bValue, aValue)
								})
								.map((event, index) => {
									const correspondingApartmentOption = apartmentOptions.find(
										(apt) => apt.value === event.shareGroupUUID,
									)

									const expenseAmount =
										Number(event.bankExpenseAmount || 0) +
										Number(event.otherExpenseAmount || 0)

									const amountColumnValue =
										event.type === LoanEventType.SHAREGROUP_DEBT_REPAYMENT
											? event.amount - expenseAmount
											: event.amount
									return (
										<Fragment key={event.uuid}>
											<LoanEventRow
												onClick={() => onEventClick(event)}
												$filledBackground={index % 2 === 0}
											>
												<TableColumn>{formatDate(event.eventDate)}</TableColumn>
												<TableColumn>
													{correspondingApartmentOption
														? `${loanEventTypeMessage(intl, event.type)} (${
																correspondingApartmentOption?.label
														  })`
														: loanEventTypeMessage(intl, event.type)}
												</TableColumn>
												<TableColumn>{amountColumnValue}</TableColumn>
											</LoanEventRow>
										</Fragment>
									)
								})
								.concat(
									<tr
										key="empty-row"
										className="full-width"
										style={{ height: 100, background: 'white' }}
									></tr>,
								)}
						</TableBody>
						<OverflowGradient style={{ bottom: 40 }} />
					</DataTable>
				</TableContainer>
			) : (
				<LoanEventsNotFoundContainer>
					{intl.formatMessage({
						defaultMessage: 'Tapahtumia ei löytynyt',
						description: 'Error text when no loan events were found',
					})}
				</LoanEventsNotFoundContainer>
			)}
		</>
	)
}

export default LoanEventsTable
